"use client";

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthLeague, AuthState, AuthUser, MeResponse } from "./types";

// ---------------------------------------------------------------------------
// Context value shape
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  /** Log in with email and password. Throws on failure. */
  login: (email: string, password: string) => Promise<void>;
  /** Register a new account. Automatically logs in on success. Throws on failure. */
  register: (email: string, password: string) => Promise<void>;
  /** Log out and clear session state. */
  logout: () => Promise<void>;
  /** Re-fetch the current user and leagues from the server. */
  refreshUser: () => Promise<void>;
  /** Set the active league by reference. */
  setActiveLeague: (league: AuthLeague) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * AuthProvider manages the client-side authentication state.
 *
 * On mount it calls the Next.js `/api/auth/me` route to check whether the
 * user has a valid session cookie. If so it hydrates the user, leagues, and
 * active league into React state. All child components can consume this
 * state via the `useAuth` hook.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [leagues, setLeagues] = useState<AuthLeague[]>([]);
  const [activeLeague, setActiveLeagueState] = useState<AuthLeague | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  /**
   * Fetch the current session from our BFF `/api/auth/me` route and
   * hydrate local state. Silently catches errors (user is simply
   * unauthenticated).
   */
  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me");

      if (!res.ok) {
        setUser(null);
        setLeagues([]);
        setActiveLeagueState(null);
        return;
      }

      const data: MeResponse = await res.json();
      setUser(data.user);
      setLeagues(data.leagues);

      // Preserve the current active league if it still exists in the
      // refreshed list; otherwise fall back to the first league.
      setActiveLeagueState((prev) => {
        if (prev && data.leagues.some((l) => l.id === prev.id)) {
          return prev;
        }
        return data.leagues[0] ?? null;
      });
    } catch {
      setUser(null);
      setLeagues([]);
      setActiveLeagueState(null);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Initial session check
  // -----------------------------------------------------------------------

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  // -----------------------------------------------------------------------
  // Auth actions
  // -----------------------------------------------------------------------

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body.error ?? "Login failed. Please check your credentials.",
        );
      }

      // Hydrate state from the fresh session.
      await refreshUser();
    },
    [refreshUser],
  );

  const register = useCallback(
    async (email: string, password: string) => {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Registration failed. Please try again.");
      }

      // The register route auto-logs-in, so we can hydrate immediately.
      await refreshUser();
    },
    [refreshUser],
  );

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setUser(null);
    setLeagues([]);
    setActiveLeagueState(null);
  }, []);

  const setActiveLeague = useCallback((league: AuthLeague) => {
    setActiveLeagueState(league);
  }, []);

  // -----------------------------------------------------------------------
  // Memoised context value
  // -----------------------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      leagues,
      activeLeague,
      isAuthenticated: user !== null,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
      setActiveLeague,
    }),
    [
      user,
      leagues,
      activeLeague,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
      setActiveLeague,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Re-export context so the hooks module can import it without a circular dep.
export { AuthContext };
